import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import {
  FormattedHTMLMessage, FormattedMessage, injectIntl, intlShape,
} from 'react-intl';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { EVENTS, FUNNEL_SOURCE_BOOK_DIRECT } from '../constants/analytics';
import { breakpoints } from '../constants/mediaqueries';
import { SPACING_VERTICAL } from '../constants/spacings';
import { fontStyles, underlineStyles } from '../constants/styles';
import { panelOrder, rowOrder, sameRow } from '../helpers/roomGrid';
import { trackEvent } from '../helpers/analytics';
import { roomParams } from '../helpers/analytics/params';
import { SELECT_STAY } from '../state/actionTypes';

import RoomPanel from './DirectBookingResults/RoomPanel';
import RoomTeaser from './RoomTeaser';


const RoomList = styled.ul`
  opacity: ${(props) => (props.dimmed ? 0.25 : 1)};
  transition: opacity 300ms ease-out;

  @media ${breakpoints.l} {
    display: flex;
    justify-content: space-between;
    flex-flow: row wrap;
  }
`;

/* Full-width row holding the expanded room's details and rates.

   The negative margin-top swallows the card's bottom margin (the same trick
   BlockWrapper uses in Event.js, same SPACING_VERTICAL values) so the panel
   attaches to its row instead of floating a full list gap below it; padding
   rather than a child margin because below l the sibling margins collapse.
   The bottom margin restores the list rhythm before the next row.

   order sorts the panel after its two-card row on the flex grid (see
   helpers/roomGrid). scroll-margin-top clears the header (5rem, as the
   picker anchor) plus the card's name row, so the "Name" the guest just
   clicked stays in view above the panel after the scroll-on-expand. */
const PanelItem = styled.li`
  width: 100%;
  margin-top: -4.6875rem;
  padding-top: 1.5rem;
  ${SPACING_VERTICAL.s};
  scroll-margin-top: 8.5rem;

  @media ${breakpoints.l} {
    margin-top: -9.375rem;
    ${SPACING_VERTICAL.l};
    order: ${(props) => props.order};
  }
`;

const SearchResults = styled.p`
  margin-bottom: 2em;
  ${fontStyles.bodySmall}

  a {
    ${underlineStyles}
  }

  /* A search that found nothing is the only thing between the picker and the
     footer. Space alone sets it apart: the picker bar and the rate rows
     already draw hairlines here, so a box of its own would just add another
     line to the stack. It stands off the bar and takes the list's vertical
     rhythm below, instead of reading as a caption stuck under the bar. */
  ${({ isOnly }) => isOnly && `
    margin-top: 2.5rem;
    ${SPACING_VERTICAL.s};
  `}
`;


class DirectBookingResults extends Component {
  constructor(props) {
    super(props);

    this.state = {
      expandedRoomId: null,
    };

    this.panelNode = null;

    this.handleChooseStay = this.handleChooseStay.bind(this);
    this.scrollToPanel = this.scrollToPanel.bind(this);
    this.setPanelNode = this.setPanelNode.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (this.props.stays !== prevProps.stays && this.state.expandedRoomId) {
      this.setState({ expandedRoomId: null }); // eslint-disable-line react/no-did-update-set-state
    }
  }

  setPanelNode(node) {
    this.panelNode = node;
  }

  scrollToPanel() {
    const node = this.panelNode;
    if (!node) return;
    // Defer a frame so the freshly mounted panel has laid out before we
    // scroll (mirrors scrollToPicker in templates/bookDirect.js). Without
    // this the panel often opens entirely below the fold and the tap looks
    // like it did nothing.
    window.requestAnimationFrame(() => {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  handleToggle(room) {
    // The list is dimmed and about to be replaced; a click now would expand
    // a panel that collapses as soon as the new results land.
    if (this.props.searchInProgress) return;

    const isOpening = this.state.expandedRoomId !== room.id;
    if (isOpening) {
      trackEvent(EVENTS.DIRECT_ROOM_EXPANDED, {
        ...roomParams({
          roomType: room.bookingId,
          roomName: room.name,
          value: room.minPrice,
        }),
        source: FUNNEL_SOURCE_BOOK_DIRECT,
      });
    }
    // The scroll runs in the setState callback, after the panel has mounted
    // and the innerRef callback has captured its node.
    this.setState({ expandedRoomId: isOpening ? room.id : null }, isOpening ? this.scrollToPanel : undefined);
  }

  handleChooseStay(ratePlan, roomType) {
    const { chooseStay, intl: { locale } } = this.props;
    chooseStay({
      language: locale,
      ratePlan,
      roomType,
      source: FUNNEL_SOURCE_BOOK_DIRECT,
    });
  }

  render() {
    const { expandedRoomId } = this.state;
    const {
      buttonColor, rooms, roomsSlug, searchInitiated, searchInProgress, stays,
    } = this.props;

    if (!searchInitiated && !searchInProgress) return null;

    const search = typeof window !== 'undefined' ? window.location.search : '';

    const availableRoomTypes = Object.keys(stays);

    const availableRooms = rooms
      .filter((r) => availableRoomTypes.includes(r.bookingId))
      .map((r) => ({
        ...r,
        minPrice: Math.min(...Object.values(stays[r.bookingId]).map((s) => parseFloat(s.total))),
      }))
      .sort((a, b) => a.minPrice - b.minPrice);

    const unavailableRooms = rooms.filter((r) => !availableRoomTypes.includes(r.bookingId));
    const sortedRooms = availableRooms.concat(unavailableRooms);
    // The card sharing a row with the expanded one fades, so the guest can
    // tell which of the two they opened (desktop only, see RoomTeaser).
    const expandedIndex = sortedRooms.findIndex((r) => r.id === expandedRoomId);

    /* Rooms are listed only when a search actually found some. With nothing to
       show, listing every room would offer only "View Room" links into the
       room template and its own booking widget, leading the guest away from
       the picker they need to change; that applies to the searching state too,
       where those cards were standing in as a loading skeleton. A re-search
       keeps the previous results in place (dimmed) until the new ones land. */
    const showRooms = availableRoomTypes.length > 0;

    // The CMS message covers both cases: a room count when there is
    // availability, the "no online availability" copy with the reservations
    // contact details when there is none.
    const summary = (
      <FormattedHTMLMessage
        defaultMessage="<strong>{numRoomsAvailable, plural, =0 {No rooms} one {1 room} other {# rooms}}</strong> available for your stay."
        id="booking.resultsSummary"
        values={{ numRoomsAvailable: availableRoomTypes.length }}
      />
    );

    return (
      <Fragment>
        <Choose>
          <When condition={searchInProgress}>
            <SearchResults>
              <FormattedMessage defaultMessage="Searching..." id="booking.roomsSearching" />
            </SearchResults>
          </When>
          <When condition={showRooms}>
            <SearchResults>{summary}</SearchResults>
          </When>
          <Otherwise>
            <SearchResults isOnly>{summary}</SearchResults>
          </Otherwise>
        </Choose>
        <If condition={showRooms}>
          <RoomList dimmed={searchInProgress}>
            <For each="room" index="idx" of={sortedRooms}>
              <Fragment key={`room_${room.id}`}>
                <RoomTeaser
                  disabled={!!Object.keys(stays).length && !stays[room.bookingId]}
                  index={idx}
                  isDimmed={expandedIndex >= 0 && sameRow(idx, expandedIndex)}
                  isExpanded={expandedRoomId === room.id}
                  onSelect={stays[room.bookingId] ? () => this.handleToggle(room) : undefined}
                  order={rowOrder(idx)}
                  panelId={`book-direct-panel-${room.id}`}
                  room={room}
                  roomPath={`/${roomsSlug}/${room.slug}${search}`}
                  stays={stays[room.bookingId]}
                />
                <If condition={expandedRoomId === room.id && !!stays[room.bookingId]}>
                  <PanelItem id={`book-direct-panel-${room.id}`} innerRef={this.setPanelNode} order={panelOrder(idx)}>
                    <RoomPanel
                      buttonColor={buttonColor}
                      details={room.details}
                      name={room.name}
                      onChooseStay={this.handleChooseStay}
                      roomPath={`/${roomsSlug}/${room.slug}${search}`}
                      roomType={room.bookingId}
                      stays={stays[room.bookingId]}
                    />
                  </PanelItem>
                </If>
              </Fragment>
            </For>
          </RoomList>
        </If>
      </Fragment>
    );
  }
}

DirectBookingResults.propTypes = {
  buttonColor: PropTypes.string.isRequired,
  chooseStay: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  rooms: PropTypes.array.isRequired,
  roomsSlug: PropTypes.string.isRequired,
  searchInitiated: PropTypes.bool.isRequired,
  searchInProgress: PropTypes.bool.isRequired,
  stays: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => {
  const { searchInitiated, searchInProgress, stays } = state.availability;
  return { searchInitiated, searchInProgress, stays };
};

const mapDispatchToProps = (dispatch) => ({
  chooseStay: (payload) => dispatch({ type: SELECT_STAY, payload }),
});

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(DirectBookingResults));
